import { Box } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import {
  CellContext,
  ColumnFiltersState,
  Row,
  SortingState,
  Table as TableType,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import inputStyles from '@/components/Input/Input.module.css';
import sharedTableStyles from '@/components/Tables/SharedTableStyles.module.css';
import styles from './LinkedBundleTableCard.module.css';
import { TableCardProps } from './LinkedBundleTableCard.types';
import SortIcon from '@/components/Tables/SortIcon';
import Tooltip from '@/components/Tooltip/Tooltip';
import Checkbox from '@/components/Checkbox/Checkbox';
import { useTranslation } from 'react-i18next';

const LinkedBundleTableCard = <TData extends object>({
  data = [],
  columns,
  returnedRecordsText,
  enableFilters = false,
  enableRowSelection = false,
  enableMultiRowSelection = true,
  noRowsMessage = '',
  title,
  className,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  width,
  itemIdentifier = 'none',
  includeMinWidth = true,
  accessTableApi,
  showPagination = false,
  meta,
}: TableCardProps<TData>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const id = useId();

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const LoadingSkeleton = useCallback(
    ({ column }: CellContext<TData, unknown>) =>
      column.id === 'id' ? <div /> : <div className={sharedTableStyles.skeleton} data-testid="loading-skeleton-cell" />,
    [],
  );

  const renderCheckboxCell = useCallback(
    (params: { row: Row<unknown> }) => (
      <div className={sharedTableStyles.rowCheckbox}>
        <Checkbox
          label=""
          aria-label="select row"
          id={`${id}-row-${params.row.id}-checkbox`}
          checked={params.row.getIsSelected()}
          onChange={params.row.getToggleSelectedHandler()}
          disabled={!params.row.getCanSelect()}
        />
      </div>
    ),
    [id],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => (
      <div className={sharedTableStyles.headerCheckbox}>
        <Checkbox
          label=""
          aria-label="select all rows"
          id={`${id}-header-checkbox`}
          checked={params.table.getIsAllRowsSelected()}
          onChange={params.table.getToggleAllRowsSelectedHandler()}
        />
      </div>
    ),
    [id],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      {
        id: 'select',
        header: enableMultiRowSelection ? renderHeaderCell : undefined,
        cell: renderCheckboxCell,
      },
      ...columns,
    ],
    [columns, enableMultiRowSelection, renderCheckboxCell, renderHeaderCell],
  );

  const tableData = useMemo(() => (isLoading ? Array(30).fill({}) : data), [isLoading, data]);

  const tableColumns = useMemo(() => {
    if (isLoading) {
      return columns.map((column) => ({
        ...column,
        cell: LoadingSkeleton,
      }));
    }
    return enableRowSelection ? columnsWithRowSelection : columns;
  }, [isLoading, enableRowSelection, columnsWithRowSelection, columns, LoadingSkeleton]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    initialState: {
      pagination: showPagination
        ? {
            pageIndex: 0,
            pageSize: 5,
          }
        : {},
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : undefined,
    meta,
    debugTable: false,
  });

  useEffect(() => {
    if (accessTableApi) accessTableApi(table);
  }, [table, accessTableApi]);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  const recordText = useMemo(() => {
    if (returnedRecordsText) {
      return returnedRecordsText(filteredRowCount);
    }
    return t('common.table.row', { count: filteredRowCount });
  }, [returnedRecordsText, filteredRowCount, t]);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientWidth === tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(false);
    }
    if (tableContainerRef.current.clientWidth < tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(true);
    }
    if (tableContainerRef.current.clientHeight === tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(false);
    }
    if (tableContainerRef.current.clientHeight < tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  return (
    <div
      style={{ width }}
      className={classNames(styles.card, {
        [className!]: className !== undefined,
        [styles.cardFilled]: showPagination,
        [sharedTableStyles.minWidth]: includeMinWidth,
      })}
    >
      <div
        ref={tableContainerRef}
        className={classNames(styles.tableContainer, {
          [styles.noTitle]: title === undefined,
          [styles.loading]: isLoading,
          [styles.tableFilled]: showPagination,
          [styles.withVerticalScrollBar]: hasVerticalScrollbar,
        })}
      >
        <table className={classNames(styles.table, { [styles.withRowSelection]: enableRowSelection })}>
          {title === 'All Bundles' ? (
            <caption
              className={classNames(styles.tableTitle, {
                [styles.noTitleCaption]: title === undefined,
                [styles.noFiltersCaption]: !enableFilters && title === undefined,
              })}
              style={!enableFilters && title === undefined ? { width: `calc(${width} - 82px)` } : undefined}
            >
              <div className={styles.test}>
                <div>{title}</div>
              </div>
            </caption>
          ) : (
            <caption
              className={classNames(styles.tableTitle, {
                [styles.noTitleCaption]: title === undefined,
                [styles.noFiltersCaption]: !enableFilters && title === undefined,
              })}
              style={!enableFilters && title === undefined ? { width: `calc(${width} - 82px)` } : undefined}
            >
              {title}
            </caption>
          )}
          <thead className={styles.tableHead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    data-testid={`table-header-${header.id}`}
                    key={header.id}
                    className={classNames(sharedTableStyles.columnHeader, {
                      [styles.checkboxColumnHeader]: enableRowSelection && index === 0,
                    })}
                    aria-sort={getAriaSort(header.column.getIsSorted())}
                  >
                    <div
                      data-testid={`table-sort-button-${header.id}`}
                      className={sharedTableStyles.columnHeaderTextAndIcon}
                      tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                      aria-label={`sort column ${
                        typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                      }`}
                      onClick={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      onTouchStart={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      role="button"
                      onKeyDown={(event: KeyboardEvent) => {
                        switch (event.key) {
                          case ' ':
                          case 'Enter':
                            header.column.toggleSorting();
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      <span className={sharedTableStyles.columnHeaderText}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.columnDef.enableSorting && <SortIcon />}
                      {header.column.columnDef.meta?.tooltipText !== undefined && (
                        <Tooltip content={header.column.columnDef.meta?.tooltipText} size={16} />
                      )}
                    </div>
                    {header.column.getCanFilter() ? (
                      <input
                        disabled={header.column.columnDef.meta?.disableFilter}
                        onChange={(event) => header.column.setFilterValue(event.target.value)}
                        value={(header.column.getFilterValue() as string) ?? ''}
                        className={classNames(inputStyles.input, sharedTableStyles.headerFilter)}
                        aria-label={`${header.column.columnDef.header} filter input`}
                        id={`${header.id}filterInput`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.original[itemIdentifier] || row.id}
                className={classNames(sharedTableStyles.row, {
                  [sharedTableStyles.even]: index % 2 === 0,
                  [sharedTableStyles.selectedRow]: row.getIsSelected(),
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={sharedTableStyles.cell} style={{ minWidth: '0px' }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {!isLoading && filteredRowCount === 0 && <caption className={styles.noRowsMessage}>{noRowsMessage}</caption>}
        </table>
      </div>
      <div className={showPagination ? styles.footerWithPagination : styles.footer}>
        {!isLoading && (
          <div>
            {showPagination ? (
              <Box pr={4}>
                <TablePagination
                  component="div"
                  size="small"
                  count={data.length}
                  page={table.getState().pagination.pageIndex}
                  onPageChange={(e, page) => table.setPageIndex(page)}
                  rowsPerPage={table.getState().pagination.pageSize || 5}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
                  sx={{
                    '.MuiTablePagination-actions button svg': {
                      height: '24px',
                      width: '24px',
                    },
                    '.MuiToolbar-root .MuiSelect-select': {
                      fontFamily: 'Open Sans',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: '2rem',
                      lineHeight: '24px',
                    },
                  }}
                />
              </Box>
            ) : (
              <h2>{`${filteredRowCount} ${recordText}`}</h2>
            )}
          </div>
        )}
      </div>
      {hasHorizontalScrollbar && <div className={sharedTableStyles.scrollShadow} />}
    </div>
  );
};

export default LinkedBundleTableCard;
